<template>
  <a-popover
    v-bind="attrs"
    v-model:open="newOpen"
    :arrow="false"
    trigger="click"
    :placement="placement"
    :overlay-class-name="`${bem.b()}`"
  >
    <a-badge :dot="useBadge && activeState" :offset="[-2, 2]">
      <ll-icon-base-pro
        :class="`${bem.e('icon')} ${bem.is(
          newOpen || activeState ? 'active' : ''
        )}`"
        :show-tip="false"
        :icon-name="sortIcon || 'icon-sort'"
        bg-color="var(--color-fill-tertiary)"
        hover-bg-color="var(--color-fill-secondary)"
        :size="size"
        :label="label"
        :icon-size="16"
      />
    </a-badge>

    <template #title>设置排序条件</template>

    <template #content>
      <a-form-item-rest>
        <draggable
          v-model="sortList"
          :animation="200"
          :class="bem.e('content')"
          style="min-width: 350px"
          item-key="key"
          handle=".handle"
        >
          <template #item="{ element, index }">
            <div :class="bem.e('list-item')">
              <!-- 拖拽图标 -->
              <div :class="['handle', bem.e('drag-icon')]">
                <ll-icon icon-name="icon-drag" />
              </div>

              <!-- 字段名 -->
              <ll-select
                v-model:value="element.key"
                style="width: 180px"
                placeholder="请选择字段"
                size="small"
                :field-names="{
                  label: 'title',
                  value: 'key',
                  options: 'options',
                }"
                :options="
              localList.map((i:any) => ({
                ...i,
                disabled: sortList.some((y) => y.key === i.key),
              }))
            "
                @select="handleField"
              />

              <!-- 排序 -->
              <ll-segmented
                v-model:value="element.orderBy"
                :class="bem.e('segmented')"
                size="small"
                :options="steps"
              />

              <!-- 移除图标 -->
              <ll-icon
                icon-name="icon-remove"
                :class="bem.e('remove-icon')"
                :size="14"
                @click="handleRemove(index)"
              />
            </div>
          </template>
        </draggable>

        <ll-empty
          v-if="sortList.length === 0"
          :style="{ margin: '12px 0' }"
          image-width="120px"
        />

        <div :class="bem.e('operate')">
          <ll-button
            :class="bem.e('operate-btn')"
            :disabled="disabledAdd"
            type="text"
            size="small"
            :icon-space="8"
            @click="handleAdd"
          >
            <ll-icon icon-name="icon-list-add" />
            {{ addSortText || '添加排序条件 ' }}
          </ll-button>

          <!-- 操作区插槽 -->
          <div :class="bem.e('operate-slot')">
            <slot name="operate" />
          </div>
        </div>
      </a-form-item-rest>
    </template>
  </a-popover>
</template>

<script lang="ts" setup>
import { createNamespace } from '@ll-plus/utils'
import { computed, ref, useAttrs, watch } from 'vue'
import { customSorterProps } from './custom-sorter'

import draggable from 'vuedraggable'

defineOptions({ name: 'LlCustomSorter' })

const props = defineProps(customSorterProps)
const emits = defineEmits(['update:open', 'field-select'])

const bem = createNamespace('custom-sorter')

const attrs = useAttrs()

// desc / asc
const sortList = ref([] as any)

const localList = computed(() => [...props.fieldList])

const steps = computed(() => [
  {
    title: props.ascText || '升序',
    key: 'asc',
    value: 'asc',
  },
  {
    title: props.descText || '降序',
    key: 'desc',
    value: 'desc',
  },
])

const newOpen = computed({
  get() {
    return props.open
  },
  set(o: boolean) {
    emits('update:open', o)
  },
})

// 禁用新增
const disabledAdd = computed(() => {
  return (
    localList.value.length === sortList.value.length ||
    (props.maxSearchCount > 0 && props.maxSearchCount <= sortList.value.length)
  )
})

// 新增
const handleAdd = () => {
  sortList.value.push({ key: null, orderBy: 'asc' })
}

// 移除
const handleRemove = (idx: number) => {
  sortList.value.splice(idx, 1)
}

// 切换字段
const handleField = (value: string, option: any[]) => {
  emits('field-select', value, option)
}

// 获取当前查询值
const getValue = () => {
  return sortList.value
}

// 清除查询
const clearValue = (range: 'all' | 'field', fields?: string[]) => {
  if (range === 'field') {
    if (fields && fields?.length > 0) {
      for (let i = sortList.value.length - 1; i >= 0; i--) {
        if (fields.includes(sortList.value[i].key)) {
          sortList.value.splice(i, 1)
        }
      }
    }
  } else {
    sortList.value.length = 0
  }
}

watch(
  () => props.fieldList,
  () => {
    clearValue('all')
  },
  { deep: true }
)

defineExpose({ getValue, clearValue })
</script>
